import { useState } from 'react';
import { SelectedEntity } from '../types';

interface UseMultiSelectProps {
  onFolderSelect: (folderId: string | null) => void;
  onItemSelect?: (item: any) => void;
}

interface UseMultiSelectReturn {
  selectedEntities: Set<string>;
  lastSelectedEntity: SelectedEntity | null;
  handleEntityClick: (entity: SelectedEntity, event?: React.MouseEvent) => void;
  setSelectedEntities: React.Dispatch<React.SetStateAction<Set<string>>>;
  setLastSelectedEntity: React.Dispatch<React.SetStateAction<SelectedEntity | null>>;
}

/**
 * Hook to manage multi-selection of folders and items
 */
export const useMultiSelect = ({
  onFolderSelect,
  onItemSelect,
}: UseMultiSelectProps): UseMultiSelectReturn => {
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());
  const [lastSelectedEntity, setLastSelectedEntity] = useState<SelectedEntity | null>(null);

  const handleEntityClick = (entity: SelectedEntity, event?: React.MouseEvent) => {
    if (event?.shiftKey) {
      // Shift+click: multi-select
      setSelectedEntities(prev => {
        const newSet = new Set(prev);
        if (newSet.has(entity.id)) {
          newSet.delete(entity.id);
        } else {
          newSet.add(entity.id);
        }
        return newSet;
      });
      setLastSelectedEntity(entity);
    } else if (event?.ctrlKey || event?.metaKey) {
      // Ctrl/Cmd+click: add to selection
      setSelectedEntities(prev => {
        const newSet = new Set(prev);
        if (newSet.has(entity.id)) {
          newSet.delete(entity.id);
        } else {
          newSet.add(entity.id);
        }
        return newSet;
      });
      setLastSelectedEntity(entity);
    } else {
      // Regular click: single select
      setSelectedEntities(new Set([entity.id]));
      setLastSelectedEntity(entity);
      
      // Call original handlers
      if (entity.type === 'folder') {
        onFolderSelect(entity.id === 'root' ? null : entity.id);
      } else if (entity.type === 'item' && onItemSelect && entity.data) {
        onItemSelect(entity.data);
      }
    }
  };

  return {
    selectedEntities,
    lastSelectedEntity,
    handleEntityClick,
    setSelectedEntities,
    setLastSelectedEntity,
  };
};
